import { RcaApiError, RcaNetworkError, toProblem } from "./errors.js";

/**
 * HTTP の下請け（FR-2 / Q6）。
 *
 * `fetch` と Web 標準しか使わないので、Node / ブラウザ / Workers で同じに動く。
 * テストでは `fetch` を差し替えて、切断・413・429 の経路を再現する。
 */

export interface RetryOptions {
  /** 再試行の上限（既定 3）。0 で無効。 */
  readonly maxAttempts?: number;
  /** 初回の待ち時間（ミリ秒。既定 200）。以降は倍々。 */
  readonly baseDelayMs?: number;
  /** テストで待たないための差し替え口。 */
  readonly sleep?: (ms: number) => Promise<void>;
}

export interface HttpOptions {
  readonly baseUrl?: string;
  /** 呼び出しごとに取得する。差し替え・更新に対応するため（FR-2.2）。 */
  readonly getToken?: () => string | null | Promise<string | null>;
  readonly fetch?: typeof globalThis.fetch;
  readonly onUnauthorized?: () => void;
  readonly retry?: RetryOptions;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** DOM の `BodyInit` に依存しない形にしておく（利用側の tsconfig を選ばない）。 */
  readonly body?: string | Uint8Array | ArrayBuffer | Blob | ReadableStream<Uint8Array> | null;
  /**
   * 冪等キー。**これが無い POST は再試行しない**（Q6）。
   * 応答が返る前に切れた場合、サーバ側では成功しているかもしれず、
   * 盲目的に送り直すと Run が二重に作られる。
   */
  readonly idempotencyKey?: string;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 200;

/** 再試行してよい HTTP ステータス。 */
const RETRIABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);

/** 本体を持たないので何度送っても同じ結果になるメソッド。 */
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "PUT", "DELETE", "OPTIONS"]);

export class HttpClient {
  readonly #fetch: typeof globalThis.fetch;
  readonly #baseUrl: string;

  constructor(private readonly options: HttpOptions = {}) {
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
  }

  /** そのリクエストを再試行してよいか（Q6）。 */
  #canRetry(method: string, init: RequestOptions): boolean {
    if (IDEMPOTENT_METHODS.has(method)) return true;
    return init.idempotencyKey !== undefined;
  }

  async #headers(init: RequestOptions): Promise<Headers> {
    const headers = new Headers(init.headers);
    const token = await this.options.getToken?.();
    if (token !== null && token !== undefined) {
      headers.set("authorization", `Bearer ${token}`);
    }
    if (init.body !== undefined && init.body !== null && !headers.has("content-type")) {
      // JSON 以外（バイト列・ストリーム）は呼び出し側が明示する。
      if (typeof init.body === "string") headers.set("content-type", "application/json");
    }
    if (init.idempotencyKey !== undefined) {
      headers.set("idempotency-key", init.idempotencyKey);
    }
    return headers;
  }

  /** 生の応答を返す（SSE や成果物のように JSON でないもの用）。 */
  async raw(path: string, init: RequestOptions = {}): Promise<Response> {
    const method = (init.method ?? "GET").toUpperCase();
    const retry = this.options.retry ?? {};
    const maxAttempts = retry.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    const sleep = retry.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
    const canRetry = this.#canRetry(method, init);

    let lastError: unknown;

    for (let attempt = 1; ; attempt += 1) {
      let response: Response;
      try {
        const { idempotencyKey: _key, body, ...rest } = init;
        response = await this.#fetch(`${this.#baseUrl}${path}`, {
          ...rest,
          method,
          headers: await this.#headers(init),
          // 本文の型は DOM の `BodyInit` に依存しない形で受けているので、
          // 実行時に等価なものとして渡す。
          ...(body === undefined ? {} : { body: body as never }),
        });
      } catch (cause) {
        lastError = new RcaNetworkError(`${method} ${path} に到達できませんでした`, cause);
        if (!canRetry || attempt >= maxAttempts) throw lastError;
        await sleep(delayFor(attempt, retry));
        continue;
      }

      if (response.ok) return response;

      if (response.status === 401) this.options.onUnauthorized?.();

      const shouldRetry = canRetry && RETRIABLE_STATUS.has(response.status) && attempt < maxAttempts;
      if (!shouldRetry) throw new RcaApiError(await toProblem(response));

      await sleep(retryAfterMs(response) ?? delayFor(attempt, retry));
    }
  }

  /** JSON を返す口。204 は `undefined`。 */
  async request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const response = await this.raw(path, init);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

function delayFor(attempt: number, retry: RetryOptions): number {
  return (retry.baseDelayMs ?? DEFAULT_BASE_DELAY_MS) * 2 ** (attempt - 1);
}

/** `Retry-After`（秒 or 日時）に従う。読めなければ null。 */
function retryAfterMs(response: Response): number | null {
  const header = response.headers.get("retry-after");
  if (header === null) return null;

  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds * 1000;

  const at = Date.parse(header);
  return Number.isFinite(at) ? Math.max(0, at - Date.now()) : null;
}

/** クエリ文字列を組み立てる。undefined は落とす。 */
export function query(params: Readonly<Record<string, string | number | undefined>>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number] => entry[1] !== undefined,
  );
  if (entries.length === 0) return "";
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&")}`;
}
